import { validateEmail } from '../lib';

export const minAdvanceDays = (encuadre) => (encuadre === 'WC' ? 3 : 14);

export const computeDayRange = (startStr, endStr) => {
  if (!startStr || !endStr) return null;
  try {
    const start = new Date(startStr);
    const end = new Date(endStr);
    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(0, 0, 0, 0);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  } catch {
    return null;
  }
};

export const canCancelRequest = (status) => !status || status === 'PENDIENTE';

export const resolveBossField = (bosses, selectedEmail) => {
  const selectedBoss = (bosses || []).find((boss) => boss.email === selectedEmail);
  return {
    selectedBoss: selectedBoss || null,
    bossField: selectedBoss ? selectedBoss.email : null,
  };
};

export const evaluateRequestRules = ({ formData, userData, now = new Date() }) => {
  if (!userData || userData.length === 0) {
    return {
      ok: false,
      variant: 'danger',
      title: 'Datos no disponibles',
      message: 'No se pudo obtener los datos del usuario.',
    };
  }

  if (formData.email && !validateEmail(formData.email)) {
    return {
      ok: false,
      variant: 'danger',
      title: 'Email inválido',
      message: 'Por favor ingrese un email válido.',
    };
  }

  if (!formData.startDate || !formData.endDate) {
    return {
      ok: false,
      variant: 'warning',
      title: 'Fechas requeridas',
      message: 'Selecciona la fecha de inicio y la fecha de fin.',
    };
  }

  if (new Date(formData.endDate) < new Date(formData.startDate)) {
    return {
      ok: false,
      variant: 'warning',
      title: 'Rango de fechas inválido',
      message: 'La fecha de fin no puede ser anterior a la fecha de inicio.',
    };
  }

  const totalVacationDaysRemaining = userData.reduce(
    (total, record) => total + (record.vacationDaysRemaining || 0),
    0
  );
  const totalEconomicDaysRemaining = userData.reduce(
    (total, record) => total + (record.economicDaysRemaining || 0),
    0
  );

  const formattedStartDate = formData.startDate ? new Date(formData.startDate) : null;
  const formattedEndDate = formData.endDate ? new Date(formData.endDate) : null;
  const encounter = userData[0]?.fields?.Encuadre;
  const requiredAdvanceDays = minAdvanceDays(encounter);
  const minAdvanceMillis = requiredAdvanceDays * 24 * 60 * 60 * 1000;

  if (formattedStartDate && formattedStartDate.getTime() - now.getTime() < minAdvanceMillis) {
    return {
      ok: false,
      variant: 'warning',
      title: 'Anticipación insuficiente',
      message: `Las solicitudes de vacaciones o días económicos deben hacerse con al menos ${requiredAdvanceDays} días de anticipación.`,
      minAdvanceDays: requiredAdvanceDays,
    };
  }

  if (totalVacationDaysRemaining > 0 && formData.permissionType === 'dia_economico') {
    return {
      ok: false,
      variant: 'warning',
      title: 'Prioridad de vacaciones',
      message: 'Debes consumir tus días de vacaciones antes de usar los días económicos.',
    };
  }

  let permissionType = formData.permissionType;
  if (totalVacationDaysRemaining > 0) {
    permissionType = 'vacaciones';
  } else if (totalEconomicDaysRemaining > 0) {
    permissionType = 'dia_economico';
  } else {
    return {
      ok: false,
      variant: 'warning',
      title: 'Sin saldo',
      message: 'No tienes saldo de vacaciones ni días económicos disponibles.',
    };
  }

  return {
    ok: true,
    permissionType,
    formattedStartDate,
    formattedEndDate,
    encounter,
    minAdvanceDays: requiredAdvanceDays,
    totalVacationDaysRemaining,
    totalEconomicDaysRemaining,
  };
};

export const splitWcDateRanges = (formattedStartDate, formattedEndDate) => {
  const toUTCDate = (date) => {
    const d = new Date(date);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  };

  const cloneUTCDate = (date) => new Date(date.getTime());
  const isWeekend = (date) => {
    const day = date.getUTCDay();
    return day === 0 || day === 6;
  };

  let currentDateIter = toUTCDate(formattedStartDate);
  const endDate = toUTCDate(formattedEndDate);
  const ranges = [];
  let periodStartDate = null;

  while (currentDateIter <= endDate) {
    if (isWeekend(currentDateIter)) {
      const daysToAdd = currentDateIter.getUTCDay() === 6 ? 2 : 1;
      currentDateIter = cloneUTCDate(currentDateIter);
      currentDateIter.setUTCDate(currentDateIter.getUTCDate() + daysToAdd);
      continue;
    }

    if (!periodStartDate) {
      periodStartDate = cloneUTCDate(currentDateIter);
    }

    const nextDate = cloneUTCDate(currentDateIter);
    nextDate.setUTCDate(currentDateIter.getUTCDate() + 1);

    const isLastDay = currentDateIter.getTime() === endDate.getTime();
    const nextIsWeekend = isWeekend(nextDate);
    const nextIsAfterEndDate = nextDate > endDate;

    if ((nextIsWeekend || isLastDay || nextIsAfterEndDate) && periodStartDate) {
      ranges.push({
        start: new Date(periodStartDate.toISOString()),
        end: new Date(currentDateIter.toISOString()),
      });
      periodStartDate = null;
    }

    currentDateIter = nextDate;
  }

  return ranges;
};

export const buildRequestFields = ({ formData, permissionType, startDate, endDate, bossField }) => {
  const fields = {
    ID_Empleado: formData.employeeNumber || null,
    Nombre: formData.Nombre || null,
    EMAIL: formData.email || null,
    FECHA_INICIO: startDate,
    FECHA_FIN: endDate,
    TIPO_DE_PERMISO: permissionType || null,
    JEFE: bossField,
    APROBACION: 'PENDIENTE',
  };

  Object.keys(fields).forEach((key) => fields[key] == null && delete fields[key]);
  return fields;
};

export const filterRequestsForBoss = (records, loggedInEmail) =>
  (records || []).filter((record) => {
    const jefeEmail = record.fields?.JEFE && (record.fields.JEFE.email || record.fields.JEFE);
    return jefeEmail && String(jefeEmail).trim().toLowerCase() === String(loggedInEmail || '').trim().toLowerCase();
  });

export const filterAndSortBossRequests = (
  records,
  { searchQuery = '', filterType = 'todos', sortBy = 'jerarquico', sortOrder = 'asc', dateRangeFilter = {} } = {}
) => {
  const q = (searchQuery || '').toLowerCase();
  const filtered = (records || []).filter((record) => {
    const searchInRecord =
      q === '' ||
      record.fields.ID_Empleado?.toString().toLowerCase().includes(q) ||
      record.fields.EMAIL?.toLowerCase().includes(q) ||
      record.fields.TIPO_DE_PERMISO?.toLowerCase().includes(q);

    const filterByStatus = filterType === 'todos' || record.fields.APROBACION === filterType;

    let dateFilterMatch = true;
    if (dateRangeFilter.startDate) {
      const recordDate = new Date(record.fields.FECHA_INICIO);
      const filterDate = new Date(dateRangeFilter.startDate);
      dateFilterMatch = dateFilterMatch && recordDate >= filterDate;
    }
    if (dateRangeFilter.endDate) {
      const recordDate = new Date(record.fields.FECHA_FIN);
      const filterDate = new Date(dateRangeFilter.endDate);
      dateFilterMatch = dateFilterMatch && recordDate <= filterDate;
    }

    return searchInRecord && filterByStatus && dateFilterMatch;
  });

  return [...filtered].sort((a, b) => {
    if (sortBy === 'jerarquico') {
      const estadoA = a.fields.APROBACION || 'PENDIENTE';
      const estadoB = b.fields.APROBACION || 'PENDIENTE';

      if (estadoA === 'PENDIENTE' && estadoB !== 'PENDIENTE') return -1;
      if (estadoA !== 'PENDIENTE' && estadoB === 'PENDIENTE') return 1;

      if (estadoA === estadoB) {
        const idA = a.fields.ID_Empleado?.toString() || '';
        const idB = b.fields.ID_Empleado?.toString() || '';
        const idComparison = idA.localeCompare(idB, undefined, { numeric: true });

        if (idComparison === 0) {
          const fechaInicioA = new Date(a.fields.FECHA_INICIO || 0);
          const fechaInicioB = new Date(b.fields.FECHA_INICIO || 0);
          return fechaInicioA - fechaInicioB;
        }

        return idComparison;
      }

      return estadoA.localeCompare(estadoB);
    }

    let comparison = 0;
    switch (sortBy) {
      case 'id_empleado': {
        const idA = a.fields.ID_Empleado || '';
        const idB = b.fields.ID_Empleado || '';
        comparison = idA.toString().localeCompare(idB.toString(), undefined, { numeric: true });
        break;
      }
      case 'fecha_inicio': {
        comparison = new Date(a.fields.FECHA_INICIO || 0) - new Date(b.fields.FECHA_INICIO || 0);
        break;
      }
      case 'fecha_fin': {
        comparison = new Date(a.fields.FECHA_FIN || 0) - new Date(b.fields.FECHA_FIN || 0);
        break;
      }
      case 'tipo_permiso': {
        comparison = (a.fields.TIPO_DE_PERMISO || '').localeCompare(b.fields.TIPO_DE_PERMISO || '');
        break;
      }
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });
};

export const countRequestStatuses = (records = []) => {
  let pending = 0;
  let approved = 0;
  let rejected = 0;
  records.forEach((record) => {
    const status = record.fields?.APROBACION || 'PENDIENTE';
    if (status === 'PENDIENTE') pending += 1;
    else if (status === 'APROBADA') approved += 1;
    else if (status === 'RECHAZADA') rejected += 1;
  });
  return { total: records.length, pending, approved, rejected };
};

export const filterPastRequests = (requests = [], pastFilter = 'todas') => {
  const sorted = [...requests].sort(
    (a, b) => new Date(b.fields.FECHA_INICIO || 0) - new Date(a.fields.FECHA_INICIO || 0)
  );
  if (pastFilter === 'todas') return sorted;
  const mapping = {
    pendientes: 'PENDIENTE',
    aprobadas: 'APROBADA',
    rechazadas: 'RECHAZADA',
  };
  const want = mapping[pastFilter];
  return sorted.filter((record) => (record.fields.APROBACION || 'PENDIENTE') === want);
};

export const pageRecords = (records = [], page = 1, pageSize = 25) =>
  records.slice((page - 1) * pageSize, page * pageSize);
